import React, { useEffect, useMemo, useState } from "react";
import { Camera, Save, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { auth, signOut } from "../services/firebaseApi";
import { getUserProfile, removeUserProfile, saveUserProfile } from "../services/profileService";
import { getRecentUserActivities, logUserActivity } from "../services/activityService";
import { useAuth } from "../context/AuthContext";
import { usePopup } from "../context/PopupContext";
import { logError } from "../utils/logger";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import UserAlertRulesEditor from "../components/UserAlertRulesEditor";

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-secondary/50 p-3">
      <div className="font-display text-2xl">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</label>
      <input {...props} className="mt-2 w-full h-12 px-5 rounded-full bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent" />
    </div>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const { showPopup } = usePopup();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({
    fullName: "",
    displayName: "",
    city: "",
    phone: "",
    email: "",
    country: "India",
    timezone: "Asia/Kolkata",
    bio: "Greenhouse operator focused on heritage tomato varieties.",
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    if (!user) return;
    getUserProfile(user.uid)
      .then((data) => {
        const profileData = data || {};
        setProfile((prev) => ({
          ...prev,
          fullName: profileData.fullName || "",
          displayName: profileData.displayName || profileData.fullName || "",
          city: profileData.city || "",
          phone: profileData.phone || "",
          email: profileData.email || user.email || "",
          country: profileData.country || "India",
          timezone: profileData.timezone || "Asia/Kolkata",
          bio: profileData.bio || prev.bio,
        }));
      })
      .catch((error) => {
        logError("ProfilePage", "Failed to load profile", error, { uid: user.uid });
        showPopup({ type: "error", title: "Profile Load Failed", message: "Could not load profile details." });
      })
      .finally(() => setLoading(false));
  }, [showPopup, user]);

  useEffect(() => {
    if (!user) return;
    getRecentUserActivities(user.uid, 4)
      .then(async (rows) => {
        if (rows.length) {
          setRecentActivity(rows);
          return;
        }
        await logUserActivity(user.uid, {
          type: "auth_login",
          title: "Logged in",
          details: "Signed in to Smart Farming account.",
        });
        const retry = await getRecentUserActivities(user.uid, 4);
        setRecentActivity(retry);
      })
      .catch((error) => {
        logError("ProfilePage", "Failed to load activities", error, { uid: user.uid });
      });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!profile.fullName.trim()) {
      showPopup({ type: "warning", title: "Name Required", message: "Full name is required." });
      return;
    }
    try {
      await saveUserProfile(user.uid, profile);
      showPopup({ type: "success", title: "Profile Saved", message: "Profile updated successfully." });
    } catch (error) {
      logError("ProfilePage", "Failed to save profile", error, { uid: user.uid });
      showPopup({ type: "error", title: "Save Failed", message: "Unable to save profile right now." });
    }
  };

  const clearProfile = async () => {
    if (!user) return;
    try {
      await removeUserProfile(user.uid);
      setProfile((prev) => ({ ...prev, fullName: "", displayName: "", city: "", phone: "", email: user.email || "" }));
      showPopup({ type: "success", title: "Profile Removed", message: "Profile data has been removed." });
    } catch (error) {
      logError("ProfilePage", "Failed to remove profile", error, { uid: user.uid });
      showPopup({ type: "error", title: "Remove Failed", message: "Unable to remove profile right now." });
    }
  };

  const initial = useMemo(() => (profile.fullName || profile.displayName || "A").trim().charAt(0).toUpperCase(), [profile]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/login";
    } catch {
      window.location.href = "/login";
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "-";
    const diff = Date.now() - Number(ts);
    const min = Math.floor(diff / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
    return new Date(ts).toLocaleString();
  };

  return (
    <PageShell>
      <PageHero eyebrow="Account" title="Your" highlight="profile." description="Manage personal information used across the Smart Farming platform." />

      <PageSection variant="content">
        <div className="mx-auto max-w-5xl grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 rounded-3xl bg-card border border-border p-8 text-center shadow-soft">
            <div className="relative mx-auto h-32 w-32 rounded-full flex items-center justify-center" style={{ background: "var(--gradient-warm)" }}>
              <span className="font-display text-6xl text-accent-foreground">{initial || "A"}</span>
              <button type="button" className="absolute bottom-0 right-0 h-9 w-9 rounded-full bg-card border border-border flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors">
                <Camera className="h-4 w-4" />
              </button>
            </div>
            <h2 className="font-display text-2xl mt-5">{profile.fullName || "Aryan A."}</h2>
            <p className="text-sm text-muted-foreground">{profile.city || "Greenhouse operator"}</p>
            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              <Stat label="Devices" value="6" />
              <Stat label="Days" value="184" />
              <Stat label="Plan" value="Pro" />
            </div>
          </div>

          <form className="lg:col-span-2 rounded-3xl bg-card border border-border p-8 space-y-4 shadow-soft" onSubmit={saveProfile}>
            <h3 className="font-display text-2xl">Personal details</h3>
            {loading ? (
              <p className="text-muted-foreground">Loading profile...</p>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" value={profile.fullName} onChange={(e) => setProfile((p) => ({ ...p, fullName: e.target.value }))} />
                  <Field label="Display name" value={profile.displayName} onChange={(e) => setProfile((p) => ({ ...p, displayName: e.target.value }))} />
                  <Field label="Email" type="email" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} />
                  <Field label="Phone" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} placeholder="+91XXXXXXXXXX" />
                  <Field label="Country" value={profile.country} onChange={(e) => setProfile((p) => ({ ...p, country: e.target.value }))} />
                  <Field label="Time zone" value={profile.timezone} onChange={(e) => setProfile((p) => ({ ...p, timezone: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">Bio</label>
                  <textarea rows={3} value={profile.bio} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} className="mt-2 w-full rounded-2xl px-5 py-3 bg-secondary/50 border border-border focus:outline-none focus:ring-2 focus:ring-accent resize-none" />
                </div>
                <div className="flex gap-3 pt-2 flex-wrap">
                  <Button type="submit" variant="accent"><Save className="h-4 w-4" /> Save changes</Button>
                  <Button type="button" variant="outline" onClick={clearProfile}>Remove profile</Button>
                  <Button type="button" variant="destructive" onClick={handleLogout}>Logout</Button>
                </div>
              </>
            )}
          </form>

          <div className="lg:col-span-3 rounded-3xl bg-card border border-border p-8 shadow-soft">
            <UserAlertRulesEditor userId={user?.uid} title="My alert rules" />
          </div>

          <div className="lg:col-span-3 rounded-3xl bg-card border border-border p-8 shadow-soft">
            <h3 className="font-display text-2xl flex items-center gap-2"><Activity className="h-5 w-5 text-accent" /> Recent activity</h3>
            <div className="mt-6 space-y-3">
              {recentActivity.map((a) => (
                <div key={a.id} className="flex justify-between gap-4 py-3 border-b border-border last:border-0">
                  <div className="text-sm">{a.title || a.details || "Activity"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{formatTime(a.createdAt)}</div>
                </div>
              ))}
              {!recentActivity.length ? (
                <div className="text-sm text-muted-foreground">No recent activity yet.</div>
              ) : null}
            </div>
            <Link to="/settings" className="mt-6 inline-block"><Button variant="ghost" size="sm">Open settings →</Button></Link>
          </div>
        </div>
      </PageSection>
    </PageShell>
  );
}

